// src/pages/modules/DataEntry/EditCustomer/EditCustomer.tsx
import React from 'react'
import {
  Alert, Badge, Button, Card,Modal, Row, Spinner } from 'react-bootstrap'
import { AuthContext, dbRead, dbUpdate } from '../../../../_services/firebase'
import { PATH, getAllCustomers } from '../../../../_services/customer.service'
import {
  buildInitialValues, validatePage,
  type FormDoc, type Field,
} from '../../../../_services/formEngine'
import { formatDate, toastDanger, toastSuccess } from '../../../../_utils'
import FormFieldRenderer from '../RegisterForm/FormFieldRenderer'
import ConfirmAlert from '../../../../components/ConfirmAlert/ConfirmAlert'
import type { CustomerReg } from '../../../../_types'
import { saveLog } from '../../../../_services/firebase'
import { loadPhotos as fetchPhotos, deletePhoto } from '../../../../_services/photo.service'
import { dbSet } from '../../../../_services/firebase'
import { DB_PHOTO_ITEM } from '../../../../_config/db_paths'

// ─── Types ────────────────────────────────────────────────────
interface PhotoRecord {
  id:         string
  name:       string
  image?:     string
  base64?:    string
  url?:       string
  size?:      number
  type?:      string
  sort?:      number
  createdAt?: number
}

function toDataUrl(p: PhotoRecord): string {
  if (p.url) return p.url
  const b64  = p.image || p.base64 || ''
  const mime = p.type || 'image/jpeg'
  if (!b64) return ''
  if (b64.startsWith('data:')) return b64
  return `data:${mime};base64,${b64}`
}

function formatSize(bytes?: number) {
  if (!bytes) return ''
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ─── StepBar ──────────────────────────────────────────────────
function StepBar({ total, current, onGo }: { total: number; current: number; onGo?: (i: number) => void }) {
  return (
    <div className='d-flex gap-1 flex-wrap'>
      {Array.from({ length: total }).map((_, i) => (
        <Button key={i} size='sm'
          variant={i === current ? 'warning' : i < current ? 'success' : 'outline-secondary'}
          style={{ width: 32, padding: '2px 0', fontSize: 12 }}
          onClick={() => onGo?.(i)}
        >{i + 1}</Button>
      ))}
    </div>
  )
}

// ─── PhotoManager ─────────────────────────────────────────────
function PhotoManager({ customerId, uid }: { customerId: string; uid: string }) {
  const [photos,      setPhotos]      = React.useState<PhotoRecord[]>([])
  const [loading,     setLoading]     = React.useState(true)
  const [uploading,   setUploading]   = React.useState(false)
  const [deleting,    setDeleting]    = React.useState<string | null>(null)
  const [preview,     setPreview]     = React.useState<PhotoRecord | null>(null)
  const [confirmDel,  setConfirmDel]  = React.useState<PhotoRecord | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // load existing photos
  React.useEffect(() => {
    ;(async () => {
      try {
        const list = await fetchPhotos(customerId)
        setPhotos(list as PhotoRecord[])
      } catch { setPhotos([]) }
      finally { setLoading(false) }
    })()
  }, [customerId])

  // resize
  const resizeImage = (file: File, maxSize = 800, quality = 0.55): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        let { width, height } = img
        if (width > maxSize || height > maxSize) {
          if (width >= height) { height = Math.round(height * maxSize / width); width = maxSize }
          else                 { width  = Math.round(width  * maxSize / height); height = maxSize }
        }
        const canvas = document.createElement('canvas')
        canvas.width = width; canvas.height = height
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('resize failed')),
          'image/jpeg', quality
        )
      }
      img.onerror = reject
      img.src = url
    })

  // upload new photos
  const handleAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setUploading(true)
    try {
      const newPhotos: PhotoRecord[] = []
      for (let i = 0; i < files.length; i++) {
        const resized = await resizeImage(files[i])
        await new Promise<void>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = async () => {
            try {
              const base64 = (reader.result as string).split(',')[1]
              const ts   = Date.now()
              const rand = Math.random().toString(36).slice(2, 8)
              const key  = `photo_${ts}_${rand}`
              const rec: PhotoRecord = {
                id:         key,
                image:      base64,
                name:       files[i].name,
                size:       resized.size,
                type:       'image/jpeg',
                sort:       photos.length + i + 1,
                createdAt:  ts,
              }
              await dbSet(DB_PHOTO_ITEM(customerId, key), { ...rec, uploadedBy: uid })
              newPhotos.push(rec)
              resolve()
            } catch (err) { reject(err) }
          }
          reader.onerror = reject
          reader.readAsDataURL(resized)
        })
      }
      setPhotos((prev) => [...prev, ...newPhotos])
      await saveLog(uid, 'update', customerId, `เพิ่มรูปภาพ ${files.length} รูป`)
      toastSuccess(`เพิ่มรูป ${files.length} รูปเรียบร้อย`)
    } catch (e: any) {
      toastDanger(e?.message || 'อัปโหลดไม่สำเร็จ')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  // delete photo
  const handleDelete = async (photo: PhotoRecord) => {
    setDeleting(photo.id)
    try {
      await deletePhoto(customerId, photo.id)
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id))
      await saveLog(uid, 'update', customerId, `ลบรูปภาพ ${photo.name}`)
      toastSuccess('ลบรูปเรียบร้อย')
    } catch (e: any) {
      toastDanger(e?.message || 'ลบไม่สำเร็จ')
    } finally {
      setDeleting(null)
      setConfirmDel(null)
    }
  }

  if (loading) return (
    <div className='d-flex justify-content-center p-3'>
      <Spinner animation='border' size='sm' variant='warning' />
      <span className='ms-2 text-muted' style={{ fontSize: 12 }}>กำลังโหลดรูปภาพ...</span>
    </div>
  )

  return (
    <>
      <ConfirmAlert
        show={!!confirmDel}
        title='ยืนยันลบรูปภาพ'
        message={`ลบรูป "${confirmDel?.name}" ออก? ไม่สามารถย้อนกลับได้`}
        confirmLabel={deleting ? 'กำลังลบ...' : 'ลบ'}
        variant='danger'
        onConfirm={() => confirmDel && handleDelete(confirmDel)}
        onCancel={() => setConfirmDel(null)}
      />

      {/* Toolbar */}
      <div className='d-flex align-items-center gap-2 mb-3'>
        <input ref={inputRef} type='file' accept='image/*' multiple style={{ display: 'none' }} onChange={handleAdd} disabled={uploading} />
        <Button size='sm' variant='outline-primary' disabled={uploading} onClick={() => inputRef.current?.click()}>
          {uploading
            ? <><Spinner size='sm' animation='border' className='me-1' />กำลังอัปโหลด...</>
            : <><i className='fa-solid fa-plus me-1' />เพิ่มรูปภาพ</>
          }
        </Button>
        <span className='text-muted' style={{ fontSize: 12 }}>
          {photos.length} รูป
          {photos.length > 0 && ` · รวม ${(photos.reduce((s, p) => s + (p.size || 0), 0) / 1024).toFixed(0)} KB`}
        </span>
      </div>

      {/* Grid */}
      {photos.length === 0 && (
        <div className='text-muted' style={{ fontSize: 12 }}>ยังไม่มีรูปภาพ กดเพิ่มด้านบน</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
        {photos.map((p) => {
          const src = toDataUrl(p)
          return (
            <div key={p.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              {/* thumbnail */}
              <div style={{ cursor: 'pointer' }} onClick={() => setPreview(p)}>
                {src
                  ? <img src={src} alt={p.name} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                  : <div style={{ height: 100, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className='fa-solid fa-image' style={{ color: '#ccc', fontSize: 24 }} />
                    </div>
                }
              </div>
              {/* delete button */}
              <button
                onClick={() => setConfirmDel(p)}
                disabled={deleting === p.id}
                style={{
                  position: 'absolute', top: 4, right: 4,
                  background: 'rgba(198,40,40,0.85)', color: '#fff',
                  border: 'none', borderRadius: 999,
                  width: 22, height: 22, fontSize: 11,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {deleting === p.id ? <Spinner size='sm' animation='border' style={{ width: 12, height: 12 }} /> : '✕'}
              </button>
              {/* caption */}
              <div style={{ fontSize: 9, padding: '2px 5px', background: '#fafafa', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name}{p.size ? ` · ${formatSize(p.size)}` : ''}
              </div>
            </div>
          )
        })}
      </div>

      {/* Full preview modal */}
      <Modal show={!!preview} onHide={() => setPreview(null)} centered size='xl'>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 14 }}>
            {preview?.name}
            {preview?.size && <span className='text-muted ms-2' style={{ fontSize: 11 }}>({formatSize(preview.size)})</span>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 8, textAlign: 'center', background: '#111' }}>
          {preview && <img src={toDataUrl(preview)} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />}
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'space-between' }}>
          <div className='d-flex gap-2'>
            <Button size='sm' variant='outline-secondary'
              disabled={(photos.findIndex((p) => p.id === preview?.id)) <= 0}
              onClick={() => { const i = photos.findIndex((p) => p.id === preview?.id); if (i > 0) setPreview(photos[i - 1]) }}
            ><i className='fa-solid fa-arrow-left me-1' />ก่อนหน้า</Button>
            <Button size='sm' variant='outline-secondary'
              disabled={(photos.findIndex((p) => p.id === preview?.id)) >= photos.length - 1}
              onClick={() => { const i = photos.findIndex((p) => p.id === preview?.id); if (i < photos.length - 1) setPreview(photos[i + 1]) }}
            >ถัดไป<i className='fa-solid fa-arrow-right ms-1' /></Button>
          </div>
          <span style={{ fontSize: 12, color: '#888' }}>
            {photos.findIndex((p) => p.id === preview?.id) + 1} / {photos.length}
          </span>
          <Button size='sm' variant='outline-danger' onClick={() => { setConfirmDel(preview); setPreview(null) }}>
            <i className='fa-solid fa-trash me-1' />ลบรูปนี้
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

// ─── EditCustomer Main ────────────────────────────────────────
export default function EditCustomer() {
  const { currentUser } = React.useContext(AuthContext)
  const uid = currentUser?.uid || ''

  const [loadingList, setLoadingList] = React.useState(true)
  const [customers,   setCustomers]   = React.useState<CustomerReg[]>([])
  const [selected,    setSelected]    = React.useState<CustomerReg | null>(null)
  const [activeTab,   setActiveTab]   = React.useState<'form' | 'photos'>('form')

  const [loadingForm, setLoadingForm] = React.useState(false)
  const [form,        setForm]        = React.useState<FormDoc | null>(null)
  const [formData,    setFormData]    = React.useState<Record<string, any>>({})
  const [activePage,  setActivePage]  = React.useState(0)
  const [errors,      setErrors]      = React.useState<string[]>([])
  const [saving,      setSaving]      = React.useState(false)
  const [confirmShow, setConfirmShow] = React.useState(false)

  React.useEffect(() => {
    ;(async () => {
      try {
        const list = await getAllCustomers('reg')
        setCustomers(list.sort((a, b) => b.meta.updatedAt - a.meta.updatedAt))
      } catch (e: any) {
        toastDanger(e?.message || 'โหลดรายชื่อไม่สำเร็จ')
      } finally {
        setLoadingList(false)
      }
    })()
  }, [])

  const handleSelect = async (cus: CustomerReg) => {
    setSelected(cus)
    setActiveTab('form')
    setLoadingForm(true)
    setActivePage(0)
    setErrors([])
    try {
      const formRaw = await dbRead<any>(PATH.regForm)
      const fd: FormDoc = formRaw?.pages?.length
        ? { id: 'main', title: formRaw.title || 'แก้ไขฟอร์ม', pages: formRaw.pages }
        : { id: 'main', title: 'แก้ไขฟอร์ม', pages: [] }
      setForm(fd)
      const init = buildInitialValues(fd)
      const flat: Record<string, any> = {
        ...cus.personal, ...cus.work, ...cus.finance, ...cus.loan,
        zone: Array.isArray(cus.loan.zone) ? cus.loan.zone : [cus.loan.zone],
        ...Object.fromEntries(Object.entries(cus.guarantor).map(([k, v]) => [`guarantor_${k}`, v])),
      }
      setFormData({ ...init, ...flat })
    } catch (e: any) {
      toastDanger(e?.message || 'โหลดฟอร์มไม่สำเร็จ')
    } finally {
      setLoadingForm(false)
    }
  }

  const handleChange     = (f: Field, val: any) => setFormData((prev) => ({ ...prev, [f.name]: val }))
  const handleToggleMulti = (f: Field, v: string) => {
    const curr: string[] = Array.isArray(formData[f.name]) ? formData[f.name] : []
    const next = curr.includes(v) ? curr.filter((x) => x !== v) : [...curr, v]
    setFormData((prev) => ({ ...prev, [f.name]: next }))
  }
  const goNext = () => {
    const errs = validatePage(form, activePage, formData)
    if (errs.length) { setErrors(errs); return }
    setErrors([])
    if (activePage < (form?.pages?.length || 1) - 1) setActivePage((i) => i + 1)
  }
  const goPrev = () => { setErrors([]); setActivePage((i) => Math.max(0, i - 1)) }

  const handleSave = async () => {
    if (!selected || !uid) return
    setSaving(true)
    try {
      const patch: Partial<CustomerReg> = {
        personal: {
          fullName: formData.fullName || '', nationalId: formData.nationalId || '',
          birthDate: formData.birthDate || '', age: formData.age || '',
          phone: formData.phone || '', address: formData.address || '',
          maritalStatus: formData.maritalStatus || '', facebookLink: formData.facebookLink || '',
          locationLink: formData.locationLink || '',
        },
        work: {
          company: formData.company || '', position: formData.position || '',
          businessType: formData.businessType || '', workingPeriod: formData.workingPeriod || '',
          workAddress: formData.workAddress || '', workLocation: formData.workLocation || '',
          currentAddress: formData.currentAddress || '', sameAsIdAddress: formData.sameAsIdAddress || '',
        },
        finance: {
          dailyIncome: Number(formData.dailyIncome) || 0,
          otherIncomeDaily: Number(formData.otherIncomeDaily) || 0,
          debtDaily: Number(formData.debtDaily) || 0,
          creditCardDaily: Number(formData.creditCardDaily) || 0,
        },
        loan: {
          wantedAmount: Number(formData.wantedAmount) || 0,
          purpose: formData.purpose || '',
          zone: Array.isArray(formData.zone) ? formData.zone : [formData.zone || ''],
          installmentDays: Number(formData.installmentDays) || 30,
        },
        guarantor: {
          fullName: formData.guarantor_fullName || '', phone: formData.guarantor_phone || '',
          facebook: formData.guarantor_facebook || '', homeAddress: formData.guarantor_homeAddress || '',
          homeLocation: formData.guarantor_homeLocation || '', shopAddress: formData.guarantor_shopAddress || '',
          shopLocation: formData.guarantor_shopLocation || '', business: formData.guarantor_business || '',
        },
        meta: { ...selected.meta, updatedAt: Date.now() },
      }
      await dbUpdate(PATH.regCustomer(selected.key), patch)
      await saveLog(uid, 'update', selected.key, `แก้ไขข้อมูล ${selected.personal.fullName}`)
      toastSuccess('อัปเดตข้อมูลเรียบร้อย')
      const list = await getAllCustomers('reg')
      setCustomers(list.sort((a, b) => b.meta.updatedAt - a.meta.updatedAt))
      setSelected(null)
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  // ─── Loading / No selection ───────────────────────────────────
  if (loadingList) return (
    <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
      <Spinner animation='border' variant='warning' />
    </div>
  )

  if (!selected) {
    return (
      <div className='p-3'>
        <h5 className='mb-3'>แก้ไขฟอร์มลูกค้า</h5>
        {customers.length === 0 && <Alert variant='info' style={{ fontSize: 13 }}>ไม่มีลูกค้าในสถานะรอการตรวจสอบ</Alert>}
        <Card className='shadow-sm'>
          <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>เลือกลูกค้าที่ต้องการแก้ไขก่อน</Card.Header>
          <Card.Body style={{ padding: 0 }}>
            {customers.map((c) => (
              <div key={c.key} onClick={() => handleSelect(c)}
                style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{c.personal.fullName || '(ไม่มีชื่อ)'}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{c.personal.phone} | {formatDate(c.meta.updatedAt)}</div>
                </div>
                <Badge bg={c.meta.status === 'รอการตรวจสอบ' ? 'warning' : 'secondary'}
                  text={c.meta.status === 'รอการตรวจสอบ' ? 'dark' : undefined}
                  style={{ fontSize: 10 }}>{c.meta.status}</Badge>
              </div>
            ))}
          </Card.Body>
        </Card>
      </div>
    )
  }

  if (loadingForm) return (
    <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
      <Spinner animation='border' variant='warning' />
    </div>
  )

  const pages   = form?.pages || []
  const pg      = pages[activePage]
  const totalPg = pages.length

  return (
    <div className='p-3'>
      <ConfirmAlert
        show={confirmShow}
        title='ยืนยันการแก้ไขข้อมูล'
        message='ยืนยันการบันทึกข้อมูลที่แก้ไข?'
        confirmLabel={saving ? 'กำลังบันทึก...' : 'ยืนยัน'}
        variant='warning'
        onConfirm={handleSave}
        onCancel={() => setConfirmShow(false)}
      />

      {/* Header */}
      <div className='d-flex align-items-start justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>แก้ไขฟอร์มลูกค้า</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>
            {activeTab === 'form' ? `หน้าที่ ${activePage + 1} / ${totalPg}` : 'จัดการรูปภาพ'}
          </div>
        </div>
        {activeTab === 'form' && <StepBar total={totalPg} current={activePage} onGo={setActivePage} />}
      </div>

      {/* Customer info + tab switcher */}
      <div className='d-flex gap-2 align-items-center mb-3 flex-wrap'>
        <Badge bg='primary'   style={{ fontSize: 11 }}>ลูกค้า: {selected.personal.fullName}</Badge>
        <Badge bg='secondary' style={{ fontSize: 11 }}>เบอร์: {selected.personal.phone}</Badge>
        <Badge bg='warning' text='dark' style={{ fontSize: 11 }}>สถานะ: {selected.meta.status}</Badge>

        {/* Tab switcher */}
        <div className='d-flex gap-1 ms-auto'>
          <Button size='sm' variant={activeTab === 'form' ? 'warning' : 'outline-secondary'}
            onClick={() => setActiveTab('form')}>
            <i className='fa-solid fa-table-list me-1' />ข้อมูล
          </Button>
          <Button size='sm' variant={activeTab === 'photos' ? 'warning' : 'outline-secondary'}
            onClick={() => setActiveTab('photos')}>
            <i className='fa-solid fa-images me-1' />รูปภาพ
            {selected.meta.photoCount > 0 && (
              <Badge bg='secondary' className='ms-1' style={{ fontSize: 9 }}>{selected.meta.photoCount}</Badge>
            )}
          </Button>
          <Button variant='outline-danger' size='sm' onClick={() => setSelected(null)}>
            ล้างการเลือก
          </Button>
        </div>
      </div>

      {/* ─── Tab: Form ─────────────────────────────────────── */}
      {activeTab === 'form' && (
        <>
          {!form?.pages?.length
            ? <Alert variant='warning'>ไม่มีแบบฟอร์ม กรุณาตั้งค่าแบบฟอร์มก่อน</Alert>
            : <>
                {errors.length > 0 && (
                  <Alert variant='danger' style={{ fontSize: 13 }} onClose={() => setErrors([])} dismissible>
                    {errors.map((e, i) => <div key={i}>{e}</div>)}
                  </Alert>
                )}
                <Card className='shadow-sm mb-3'>
                  <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
                    <Badge bg='warning' text='dark' className='me-2'>โหมดแก้ไข</Badge>
                    {pg?.title || `หน้า ${activePage + 1}`}
                  </Card.Header>
                  <Card.Body>
                    <Row className='g-3'>
                      {(pg?.fields || []).map((f) => (
                        <FormFieldRenderer key={f.name} field={f}
                          value={formData[f.name]} onChange={handleChange} onToggleMulti={handleToggleMulti} />
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
                <div className='d-flex gap-2'>
                  <Button variant='outline-secondary' size='sm' onClick={goPrev} disabled={activePage === 0}>
                    <i className='fa-solid fa-arrow-left me-1' />ก่อนหน้า
                  </Button>
                  {activePage < totalPg - 1
                    ? <Button variant='warning' size='sm' onClick={goNext} className='ms-auto'>
                        ถัดไป<i className='fa-solid fa-arrow-right ms-1' />
                      </Button>
                    : <Button variant='success' size='sm' disabled={saving} onClick={() => setConfirmShow(true)} className='ms-auto'>
                        <i className='fa-solid fa-floppy-disk me-1' />บันทึกการแก้ไข
                      </Button>
                  }
                </div>
              </>
          }
        </>
      )}

      {/* ─── Tab: Photos ───────────────────────────────────── */}
      {activeTab === 'photos' && (
        <Card className='shadow-sm'>
          <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
            <i className='fa-solid fa-images me-2' />รูปภาพประกอบ
            <span className='text-muted ms-2' style={{ fontSize: 11, fontWeight: 400 }}>
              ย่อให้ไม่เกิน 800px อัตโนมัติ
            </span>
          </Card.Header>
          <Card.Body>
            <PhotoManager customerId={selected.key} uid={uid} />
          </Card.Body>
        </Card>
      )}
    </div>
  )
}